import React, { useState, useEffect, useLayoutEffect } from 'react';
import { memoize } from 'lodash';
import { APP_EVENTS } from 'Components/utils/responsive/Events.constants';
import { BREAKPOINTS, DEVICES } from './Responsive.constants';
import { getCurrentBreakpoint } from './Responsive.helper';
import { subscribe, unsubscribe } from './Responsive.listener';
import { ResponsiveContext } from './ResponsiveContext';
import getCurrentResponsive from './getCurrentResponsive';
import type { Device } from './Responsive.constants';
// prevent rerender of all component using ResponsiveContext when the breakpoint doesn't change
const memoizedGetCurrentResponsive = memoize(getCurrentResponsive);
type Props = {
  children: React.ReactNode;
  userAgent?: string | void;
  device?: Device;
};

function sendEvent(name) {
  let event;

  if (typeof Event === 'function') {
    event = new Event(name);
  } else {
    event = document.createEvent('Event');
    event.initEvent(name, true, true);
  }

  document.dispatchEvent(event);
}

export function ResponsiveLayout({ children, device }: Props) {
  // Mirror the SSR value exactly — no window.matchMedia on first render.
  // On mobile SSR the server always returns XXS; calling matchMedia here on
  // an iPhone (375px > 22.51em = XS) produces a different tree and triggers
  // React hydration error #418. The real breakpoint is set in useEffect.
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(
    device === DEVICES.DESKTOP ? BREAKPOINTS.XL : BREAKPOINTS.XXS,
  );
  const [isAppMounted, setIsAppMounted] = useState(false);

  // Fire the mounting event after DOM commit, not during render.
  // Calling document.dispatchEvent in the render phase is a side effect that
  // React 18 / Safari mobile rejects — it can interrupt reconciliation.
  useLayoutEffect(() => {
    if (!window.cmAppMounted) {
      sendEvent(APP_EVENTS.MOUNTING);
    }
  }, []);

  useEffect(() => {
    const componentLike = {
      setState(
        updater: (state: {
          currentBreakpoint: Breakpoint;
        }) => { currentBreakpoint: Breakpoint } | null,
      ) {
        setCurrentBreakpoint((prev) => {
          const result = updater({ currentBreakpoint: prev });
          if (result === null) {
            return prev;
          }
          return result.currentBreakpoint;
        });
      },
    };

    subscribe(componentLike);

    setCurrentBreakpoint(getCurrentBreakpoint(device));
    setIsAppMounted(true);

    if (!window.cmAppMounted) {
      window.cmAppMounted = true;
      sendEvent(APP_EVENTS.MOUNTED);
    }

    return () => {
      unsubscribe(componentLike);
    };
  }, []);

  const value = memoizedGetCurrentResponsive({
    currentBreakpoint,
    isAppMounted,
  });

  return <ResponsiveContext.Provider value={value}>{children}</ResponsiveContext.Provider>;
}
